import React, { useState, useMemo, useCallback } from "react";
import { Container, Row, Col, Form, Card } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import top10Data from "../../assets/data/top102.json";
import "../../App.css";
import { colorForEntit } from "../../config/colors.js"; 


const formatIntDE = (n) =>
  n === null || n === undefined
    ? "-"
    : new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(n);

const formatPctDE = (n) =>
  n === null || n === undefined
    ? "-"
    : new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(n);

const sanitize = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const HaeufigsteKrebserkrankungen2 = () => {
  // Ascending order from 2018 (2018, 2019, …)
  const availableYears = useMemo(() => {
    return [...new Set(top10Data.map((d) => Number(d.diayear)))]
      .filter((y) => y >= 2018)
      .sort((a, b) => a - b)
      .map(String);
  }, []);

  const [year, setYear] = useState(
  availableYears[availableYears.length - 1] ?? ""
);


  
  const dataForSex = useCallback(
    (sex) => {
      const rows = top10Data
        .filter(
          (d) =>
            String(d.diayear) === String(year) &&
            d.SEX === sex &&
            d.entit !== "Krebs gesamt ohne C44"
        )
        .map((d) => {
          const pct =
            d.Anteil_num !== undefined && d.Anteil_num !== null
              ? Number(d.Anteil_num)
              : d.Anteil !== undefined && d.Anteil !== null
              ? Number(String(d.Anteil).replace(",", "."))
              : null;
          const value = Number.isFinite(pct) ? pct : 0;
          return {
            entit: d.entit,
            n_wob: Number(d.n_wob),
            Anteil_num: value,
          };
        })
        .filter((r) => r.Anteil_num > 0) // drop zero-share slices
        .sort((a, b) => b.Anteil_num - a.Anteil_num); // by share desc

      return rows;
    },
    [year]
  );

  const dataM = useMemo(() => dataForSex("M"), [dataForSex]);
  const dataW = useMemo(() => dataForSex("W"), [dataForSex]);

  const tooltipCommon = useMemo(
    () => ({
      trigger: "item",
      backgroundColor: "#000",
      borderWidth: 0,
      textStyle: { color: "#fff", fontSize: 12 },
      confine: true,
      formatter: (params) => {
        const { name, value, data } = params;
        return `
          <div style="text-align:left;line-height:1.3">
            <div style="font-weight:600;margin-bottom:4px">${sanitize(name)}</div>
            <div>Anzahl Neuerkrankte (Wohnortbezug): ${formatIntDE(data?.n_wob)}</div>
            <div>Anteil der Neuerkrankungen (Wohnortbezug): ${formatPctDE(value)}%</div>
          </div>
        `;
      },
    }),
    []
  );

  const makeOption = useCallback(
    (sexLabel, rows, sexCode) => ({
      legend: { show: false },
      tooltip: tooltipCommon,
      series: [
        {
          name: sexLabel,
          type: "pie",
          radius: ["40%", "70%"],
          avoidLabelOverlap: true,
          itemStyle: { borderWidth: 0, borderColor: "transparent" },
          emphasis: { itemStyle: { borderWidth: 0, borderColor: "transparent" } },
          label: {
            show: true,
            formatter: "{b}", // show entity name
            fontSize: 12,
          },
          labelLine: { show: true, length: 12, length2: 8 },
          data: rows.map((r) => ({
            name: r.entit,
            value: r.Anteil_num,
            n_wob: r.n_wob,
            itemStyle: {
              color: colorForEntit(r.entit, sexCode), // 👈 pass sex to get sex-specific color
              borderWidth: 1,
              borderColor: "#fff",
            },
          })),
        },
      ],
    }),
    [tooltipCommon]
  );

  const optionWomen = useMemo(() => makeOption("Frauen", dataW, "W"), [makeOption, dataW]);
  const optionMen = useMemo(() => makeOption("Männer", dataM, "M"), [makeOption, dataM]);

  return (
    <Container className="mt-4">
      <h2 className="section-title">Häufigste Krebsneuerkrankungen</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Hier werden die Krebsarten für Frauen und Männer gezeigt, an denen die Menschen in
          Sachsen-Anhalt pro Jahr am häufigsten erkranken. Sie haben die Möglichkeit, sich die
          Verteilung für verschiedene Jahre anzeigen zu lassen.
        </p>
        <p>
          Abgesehen vom nicht-melanotischen Hautkrebs (C44, „weißer Hautkrebs“), dessen Diagnosen
          etwa ein Fünftel aller Neuerkrankungen im KR-LSA ausmachen, sind die häufigsten
          Krebsarten das Mammakarzinom bei den Frauen (C50) und das Prostata-Karzinom (C61) bei den
          Männern. Danach folgen die Karzinome in Kolon bzw. Lunge.
        </p>
      </div>

      <Form.Group
        controlId="yearSelect"
        className="mb-4 d-flex align-items-center gap-2"
        style={{ maxWidth: 360 }}
      >
        <Form.Label className="mb-0">Jahr:</Form.Label>
        <Form.Select
          aria-label="Jahr auswählen"
          value={year}
          onChange={(e) => setYear(e.target.value)}
        >
          {availableYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Row> 
        <Col md={6} className="mb-4">
    <Card className="h-100 border-0 shadow-none">
      <Card.Body>
        <h5 className="mb-3">Männer</h5>
        <ReactECharts
          key={`men-${year}`}
          option={optionMen}
          style={{ width: "100%", height: 420 }}
        />
      </Card.Body>
    </Card>
  </Col>
      <Col md={6} className="mb-4">
      <Card className="h-100 border-0 shadow-none">
      <Card.Body>
        <h5 className="mb-3">Frauen</h5>
        <ReactECharts
          key={`women-${year}`}
          option={optionWomen}
          style={{ width: "100%", height: 420 }}
        />
      </Card.Body>
    </Card>
  </Col>

 
</Row>
    </Container>
  );
};

export default HaeufigsteKrebserkrankungen2;
